/*
 * c_accordion.js — アコーディオン高さアニメーション
 * ナレッジ: knowledge/shopify/waapi-accordion-height-animation.md
 *
 * 鉄則:
 * - 動かす箱(.accordion__content)は padding/margin フリー + overflow:hidden
 * - 余白は inner(.accordion__inner)へ逃がし scrollHeight に内包させる
 * - JS が動かすのは単一パネルの height + opacity のみ
 * - キャレット等の付随する寸法変化は CSS transition（同 duration/easing）に任せる
 */
(() => {
  const DURATION = 380;
  const EASING = 'ease-out';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * パネルの開閉を height + opacity でアニメーションする。
   * @param {HTMLDetailsElement} details - 対象の details 要素
   * @param {HTMLElement} panel - 高さを動かす .accordion__content
   * @param {HTMLElement} summary - トグル元の summary 要素
   * @param {boolean} opening - 開く場合 true
   */
  const animatePanel = (details, panel, summary, opening) => {
    if (panel._anim) panel._anim.cancel();

    details.classList.toggle('c_accordion--open', opening);
    summary.setAttribute('aria-expanded', String(opening));

    // 開く時はコンテンツを DOM に出してから測る
    if (opening) details.open = true;

    if (reduceMotion.matches) {
      if (!opening) details.open = false;
      return;
    }

    const fromHeight = opening ? 0 : panel.scrollHeight;
    const toHeight = opening ? panel.scrollHeight : 0;

    panel.style.overflow = 'hidden';
    panel.dataset.animStatus = 'running';

    const anim = panel.animate(
      [
        { height: `${fromHeight}px`, opacity: opening ? 0 : 1 },
        { height: `${toHeight}px`, opacity: opening ? 1 : 0 },
      ],
      { duration: DURATION, easing: EASING },
    );
    panel._anim = anim;

    const cleanup = () => {
      panel.style.height = '';
      panel.style.opacity = '';
      panel.style.overflow = '';
      panel.dataset.animStatus = '';
      panel._anim = null;
    };

    anim.onfinish = () => {
      cleanup();
      if (!opening) details.open = false;
    };
    anim.oncancel = cleanup;
  };

  /**
   * 1つの .accordion__content にトグルハンドラを束ねる。
   * @param {HTMLElement} panel - .accordion__content 要素
   */
  const bindPanel = (panel) => {
    const details = panel.closest('details');
    if (!details || details.dataset.cAccordionBound) return;

    const summary = details.querySelector('summary');
    if (!summary) return;

    details.dataset.cAccordionBound = 'true';
    details.classList.toggle('c_accordion--open', details.open);
    summary.setAttribute('aria-expanded', String(details.open));

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (panel.dataset.animStatus === 'running') return;
      animatePanel(details, panel, summary, !details.open);
    });
  };

  /**
   * 指定スコープ内のアコーディオンを初期化する。
   * @param {ParentNode} [root] - 探索ルート（省略時は document）
   */
  const initAccordions = (root) => {
    const scope = root || document;
    scope.querySelectorAll('.accordion__content').forEach(bindPanel);
  };

  document.documentElement.classList.add('c_accordion-js');

  document.addEventListener('DOMContentLoaded', () => initAccordions(document));
  document.addEventListener('shopify:section:load', (event) => initAccordions(event.target));
})();
